import React, { useState } from 'react';
import styled from '@emotion/styled';
import _ from 'lodash';
import NumberInput from '~ComponentsImproved/Numpad';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useWindowSize } from '~Hooks';

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: auto;
`;

const Count = styled.div<{
  selected?: boolean;
}>`
  height: 64px;
  background: transparent;
  border-top: 1px solid var(--colorBlueGray1000);
  border-left: ${({ selected }) => (selected ? '4px solid var(--colorUIYellow)' : 'none')};
  padding: ${({ selected }) => (selected ? '0 4px 0 0' : '0')};
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;

  &:last-child {
    border-bottom: 1px solid var(--colorBlueGray1000);
  }
`;

export interface Props {
  count: number;
  onChange: (count: number) => void;
  yOffset?: number;
}

const CountSelector: React.FunctionComponent<Props> = ({ count, onChange, yOffset = 24 }) => {
  const windowSize = useWindowSize();
  const [showInput, setShowInput] = useState(false);

  // If the yOffset is greater than 24, we assume it's the multi-qsr view, which has less space
  const highestCount =
    yOffset > 24 ? (windowSize.height > 1000 ? 9 : 5) : windowSize.height > 1000 ? 10 : 6;

  const handleChange = (n: number) => {
    onChange(n);
  };

  return (
    <Wrapper>
      {_.range(1, highestCount).map((n) => (
        <Count
          data-test="count-selector"
          onTouchStart={() => handleChange(n)}
          selected={count === n}
          key={n}>
          <Heading level={4} color={count === n ? 'var(--colorWhite)' : 'var(--colorBlueGray500)'}>
            {n}
          </Heading>
        </Count>
      ))}
      <Count
        data-test="count-selector"
        selected={count > highestCount}
        onTouchStart={() => setShowInput(true)}>
        <Heading
          level={4}
          color={count > highestCount ? 'var(--colorWhite)' : 'var(--colorBlueGray500)'}>
          #
        </Heading>
      </Count>

      {showInput && (
        <NumberInput
          onSubmit={(n) => {
            handleChange(+n);
            setShowInput(false);
          }}
          decimal
          maxValue={2000}
          onClose={() => setShowInput(false)}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(CountSelector);
